// =====================
// DOM Manipulation Utils
// =====================
function l_addClassesToRteTables() {
  document.querySelectorAll('.l-rte table, .shopify-policy__body .rte table').forEach((table) => {
    const wrapper = document.createElement('div')
    wrapper.classList.add('l-rte-table', 'l-overflow-x-auto', 'l-scrollbar--hidden')
    table.parentNode.insertBefore(wrapper, table)
    wrapper.appendChild(table)
  })
}

function l_smoothScrollOnAnchorTags() {
  document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
    const id = anchor.getAttribute('href').split('#')?.[1]
    if (!id) return

    anchor.addEventListener('click', (e) => {
      e.preventDefault()

      const target = document.querySelector(anchor.getAttribute('href'))
      target?.scrollIntoView?.({ behavior: 'smooth', block: 'center', inline: 'center' })
    })
  })
}

// =====================
// DOM Observation Utils
// =====================

// =====================
// Focus Management & Trap Focus
// =====================

function l_getFocusableElements(container) {
  const focusableSelectors = `summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe`

  return Array.from(container.querySelectorAll(focusableSelectors)).filter((el) => isVisible(el))
}

function l_disableFocusableElements(container) {
  l_getFocusableElements(container).forEach((el) => {
    el.setAttribute('tabindex', '-1')
    el.setAttribute('aria-disabled', 'true')
  })
}

function l_enableFocusableElements(container) {
  l_getFocusableElements(container).forEach((el) => {
    el.removeAttribute('tabindex')
    el.removeAttribute('aria-disabled')
  })
}

const l_trapFocusHandlers = {}

function l_trapFocus(container, defaultElementToFocus, enableFocusRange = true) {
  // cleanup any previous traps
  l_removeTrapFocus()

  l_trapFocusHandlers.keydown = (event) => {
    const key = event.code?.toUpperCase()
    const isTab = key === 'TAB'
    const isArrowDown = key === 'ARROWDOWN'
    const isArrowUp = key === 'ARROWUP'

    const elements = l_getFocusableElements(container)
    const [first, last] = [elements[0], elements[elements.length - 1]]
    const currentIndex = elements.indexOf(event.target)

    if (!elements.length) return

    if (isTab) {
      if ((event.target === last || last?.contains?.(event.target)) && !event.shiftKey) {
        event.preventDefault()
        first?.focus()
      } else if ((event.target === container || first?.contains?.(event.target)) && event.shiftKey) {
        event.preventDefault()
        last?.focus()
      }

      return
    }

    if (isArrowDown) {
      event.preventDefault()
      if (currentIndex === -1 || currentIndex === elements.length - 1) {
        first?.focus()
      } else {
        elements[currentIndex + 1]?.focus()
      }

      return
    }

    if (isArrowUp) {
      event.preventDefault()
      if (currentIndex === -1 || currentIndex === 0) {
        last?.focus()
      } else {
        elements[currentIndex - 1]?.focus()
      }

      return
    }
  }

  l_trapFocusHandlers.focusin = (event) => {
    if (container.contains(event.target)) {
      document.addEventListener('keydown', l_trapFocusHandlers.keydown)
    }
  }

  l_trapFocusHandlers.focusout = () => {
    document.removeEventListener('keydown', l_trapFocusHandlers.keydown)
  }

  document.addEventListener('focusin', l_trapFocusHandlers.focusin)
  document.addEventListener('focusout', l_trapFocusHandlers.focusout)

  // Initial focus
  const elements = l_getFocusableElements(container)
  const first = elements[0]
  const elementToFocus = defaultElementToFocus || first
  if (elementToFocus) {
    elementToFocus.focus()
    if (
      elementToFocus.tagName === 'INPUT' &&
      ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
      elementToFocus.value &&
      enableFocusRange
    ) {
      elementToFocus.setSelectionRange(0, elementToFocus.value.length)
    }
  }
}

function l_removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', l_trapFocusHandlers.focusin)
  document.removeEventListener('focusout', l_trapFocusHandlers.focusout)
  document.removeEventListener('keydown', l_trapFocusHandlers.keydown)
  if (elementToFocus) elementToFocus.focus()
}

// =====================
// Scroll & Body Lock Utilities
// =====================

function l_lockBodyScroll(elementName) {
  // Set scrollbar width
  const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth
  document.documentElement.style.setProperty('--l-body-scrollbar-width', `${scrollBarWidth}px`)

  // Add classes
  document.body.classList.add('js-block-page')

  if (elementName) {
    document.body.classList.add(`js-block-page-${elementName}`)
  }
}

function l_unLockBodyScroll(elementName) {
  // Remove scrollbar width CSS variables
  document.documentElement.style.removeProperty('--l-body-scrollbar-width')

  // Remove classes
  if (elementName) {
    document.body.classList.remove(`js-block-page-${elementName}`)

    const isExistAnotherBlockedClass = Array.from(document.body.classList).some((cls) =>
      cls.startsWith('js-block-page-')
    )

    !isExistAnotherBlockedClass && document.body.classList.remove('js-block-page')
  } else {
    document.body.classList.remove('js-block-page')
  }
}

function l_setupVariablesBeforeOpenModal() {
  // Elements
  const announcementBarSection = document.getElementById('announcementBar')
  const headerSection = document.getElementById('header')?.parentElement

  // Set CSS variables
  document.documentElement.style.setProperty('--l-viewport-height', `${visualViewport?.height ?? window.innerHeight}px`)
  document.documentElement.style.setProperty('--l-header-height', `${headerSection?.offsetHeight || 0}px`)
  document.documentElement.style.setProperty(
    '--l-announcement-bar-height',
    `${announcementBarSection?.offsetHeight || 0}px`
  )

  return {
    headerHeight: headerSection?.offsetHeight || 0,
    announcementBarHeight: announcementBarSection?.offsetHeight || 0,
    headerTotalHeight: (headerSection?.offsetHeight || 0) + (announcementBarSection?.offsetHeight || 0),
    viewportHeight: visualViewport?.height ?? window.innerHeight,
  }
}

// =====================
// Media Controls
// =====================

function l_pauseAllMedia(container = document) {
  container.querySelectorAll('l-external-media').forEach((video) => {
    video?.pauseVideo?.()
  })
  container.querySelectorAll('.js-youtube').forEach((youtubeVideo) => {
    youtubeVideo.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*')
  })
  container.querySelectorAll('.js-vimeo').forEach((vimeoVideo) => {
    vimeoVideo.contentWindow.postMessage('{"method":"pause"}', '*')
  })
  container.querySelectorAll('video').forEach((video) => video.pause())
  container.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause()
  })
  container.querySelectorAll('video').forEach((video) => {
    video.pause()
  })
}

function l_playMedia(container = document) {
  container.querySelectorAll('l-external-media').forEach((video) => {
    video?.playVideo?.()
  })
  container.querySelectorAll('.js-youtube').forEach((youtubeVideo) => {
    youtubeVideo.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*')
  })
  container.querySelectorAll('.js-vimeo').forEach((vimeoVideo) => {
    vimeoVideo.contentWindow.postMessage('{"method":"play"}', '*')
  })
  container.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.play()
  })
  container.querySelectorAll('video').forEach((video) => {
    video.play()
  })
}

// =====================
// Event/Utility Functions
// =====================

function l_debounce(fn, wait) {
  let t

  return (...args) => {
    clearTimeout(t)
    t = setTimeout(() => fn.apply(this, args), wait)
  }
}

function l_fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  }
}

// =====================
// Input & User Detection
// =====================

// =====================
// String Utilities
// =====================


// =====================
// Money Formatting
// =====================

function l_formatMoney(cents, format = '') {
  let value = ''
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/
  const formatString = format || window.theme.moneyFormat || '${{amount}}'

  if (typeof cents == 'string') {
    cents = cents.replace('.', '')
  }

  function defaultOption(opt, def) {
    return typeof opt == 'undefined' ? def : opt
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultOption(precision, 2)
    thousands = defaultOption(thousands, ',')
    decimal = defaultOption(decimal, '.')

    if (isNaN(number) || number == null) {
      return 0
    }

    number = (number / 100.0).toFixed(precision)

    var parts = number.split('.'),
      dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
      cents = parts[1] ? decimal + parts[1] : ''

    return dollars + cents
  }

  switch (formatString.match(placeholderRegex)[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2)
      break
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0, ' ', '.')
      break
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',')
      break
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', '.')
      break
  }

  return formatString.replace(placeholderRegex, value)
}

// =====================
// Visitor/Session Utilities
// =====================

/***********************************
 * [FUNCTION]: OPEN KLAVIYO FORMS
 ************************************/

/************************************************************************************

* [ADD TO CART MODULE]

*************************************************************************************/

class LTACartServices {
  constructor(cartDrawer = null) {
    this.cartDrawer = cartDrawer
  }

  getBaseConfig() {
    return {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    }
  }

  createConfigFromForm(formEl) {
    const config = this.getBaseConfig()
    const formData = new FormData(formEl)

    config.body = formData

    return config
  }

  createConfigFromItems(items = []) {
    const config = this.getBaseConfig()
    const formData = new FormData()

    items.forEach((item, index) => {
      const prefix = `items[${index}]`

      formData.append(`${prefix}[id]`, item.id)
      formData.append(`${prefix}[quantity]`, item.quantity)
      if (item.selling_plan) {
        formData.append(`${prefix}[selling_plan]`, item.selling_plan)
      }

      // Add all other properties from the item object except id, quantity, and selling_plan
      Object.entries(item).forEach(([key, value]) => {
        if (['id', 'quantity', 'selling_plan'].includes(key)) return
        // If the value is an object, serialize it as JSON
        if (typeof value === 'object' && value !== null) {
          formData.append(`${prefix}[${key}]`, JSON.stringify(value))
        } else if (value !== undefined) {
          formData.append(`${prefix}[${key}]`, value)
        }
      })
    })

    config.body = formData

    return config
  }

  async addToCart(config, id = 'default') {
    // Note: Don't use finally / catch here

    return fetch(`${window.theme.routes.cart_add_url}.js`, config)
      .then((res) => res.json())
      .then((data) => {
        if (data?.status) {
          document.dispatchEvent(new CustomEvent('l:cart:error', { detail: { id, description: data.description } }))

          return data
        }

        document.dispatchEvent(new CustomEvent('l:product:added', { bubbles: true, detail: { id, response: data } }))

        return data
      })
  }

  async changeCartLineItem(formData) {
    return fetch(`${window.theme.routes.cart_change_url}.js`, { ...l_fetchConfig(), body: JSON.stringify(formData) })
      .then((res) => res.json())
      .then((data) => {
        return data
      })
  }
}

const ltaCartService = new LTACartServices()

/************************************************************************************

* [GENERAL COMPONENTS]

*************************************************************************************/

/*****************************************
 * [COMPONENT]: COLLAPSIBLE TAB
 ******************************************/

class LTACollapsibleTab extends HTMLElement {
  constructor() {
    super()
  }

  connectedCallback() {
    // Elements
    this.triggerEl = this.querySelector('[data-trigger]')
    this.contentEl = this.querySelector('[data-content]')

    // Data
    this.expandedClass = 'is-expanded'
    this.collapsedClass = 'is-collapsed'
    this.collapsedHeight = '0px'
    this.oneAtATime = true
    this.isScrollIntoView = this.dataset.scrollIntoView === 'true'

    this.transitionendEvent = this.transitionendEventName()

    // Setup default behavior / data
    this.dataset.oneOpen === 'false' && (this.oneAtATime = false)

    if (this.getAttribute('open') === 'true') {
      this.selected = true
      this.contentEl.removeAttribute('hidden')
      this.contentEl.classList.remove('l-hidden')
      this.classList.add(this.expandedClass)
      this.setExpandedAria(false)
    } else {
      this.contentEl.style.height = this.collapsedHeight
      this.classList.add(this.collapsedClass)
      this.setCollapsedAria(null, false)
    }

    // Events
    this.triggerEl.addEventListener('click', (e) => this.toggle(e))
  }

  toggle(e) {
    e.preventDefault()

    this.selected ? this.close() : this.open()

    /* Close another tab and open only the current tab. */
    if (this.selected && this.oneAtATime) {
      const tabsContainer = this.closest('[data-collapsible-wrapper]')
      const collapsibleTabs = tabsContainer?.querySelectorAll('l-collapsible-tab')

      collapsibleTabs?.length &&
        collapsibleTabs.forEach((tab) => {
          tab !== this && tab.selected && tabsContainer !== tab && tab.close()
        })
    }
  }

  open() {
    this.selected = true
    this.expand()

    this.setExpandedAria()
  }

  close() {
    this.selected = false
    this.collapse()

    this.setCollapsedAria()
  }

  expand() {
    const onTransitionEnd = ((event) => {
      if (event.target === this.contentEl) {
        this.contentEl.removeEventListener(this.transitionendEvent, onTransitionEnd)

        this.isOpen &&
          requestAnimationFrame(() => {
            this.contentEl.style.transition = '0'
            this.contentEl.style.height = 'auto'

            requestAnimationFrame(() => {
              this.contentEl.style.height = null
              this.contentEl.style.transition = null
            })
          })
      }
    }).bind(this)

    this.contentEl.addEventListener(this.transitionendEvent, onTransitionEnd)
    this.isOpen = true

    this.contentEl.removeAttribute('hidden')
    this.contentEl.classList.remove('l-hidden')

    this.classList.add(this.expandedClass)
    this.classList.remove(this.collapsedClass)

    this.contentEl.style.height = this.contentEl.scrollHeight + 'px'
  }

  collapse() {
    const onTransitionEnd = ((event) => {
      if (event.target === this.contentEl) {
        this.contentEl.removeEventListener(this.transitionendEvent, onTransitionEnd)

        if (!this.isOpen) {
          this.classList.add(this.collapsedClass)
        }

        this.contentEl.setAttribute('hidden', 'hidden')
        this.contentEl.classList.add('l-hidden')
      }
    }).bind(this)

    this.contentEl.addEventListener(this.transitionendEvent, onTransitionEnd)
    this.isOpen = false
    this.classList.remove(this.expandedClass)

    requestAnimationFrame(() => {
      this.contentEl.style.transition = '0'
      this.contentEl.style.height = this.contentEl.scrollHeight + 'px'

      requestAnimationFrame(() => {
        this.contentEl.style.transition = null
        this.contentEl.style.height = this.collapsedHeight
      })
    })

    if (this.isScrollIntoView) {
      l_debounce(() => {
        this.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 400)()
    }
  }

  destroy() {
    this.triggerEl && this.triggerEl.removeEventListener('click', (e) => this.toggle(e))
    this.contentEl && this.contentEl.removeAttribute('aria-hidden')
    this.contentEl && (this.contentEl.style.height = 'auto')

    this.classList.remove(this.expandedClass, this.collapsedClass)
    this.removeAttribute('open')
  }

  transitionendEventName() {
    let eventName,
      testElement = document.createElement('div'),
      eventNames = {
        transition: 'transitionend',
        OTransition: 'otransitionend',
        MozTransition: 'transitionend',
        WebkitTransition: 'webkitTransitionEnd',
      }

    for (eventName in eventNames) {
      if (eventNames.hasOwnProperty(eventName) && testElement.style[eventName] !== false) {
        return eventNames[eventName]
      }
    }
  }

  setExpandedAria(autoFocus = true) {
    this.setAttribute('open', true)

    this.triggerEl.setAttribute('aria-expanded', 'true')
    this.contentEl.removeAttribute('aria-hidden')
    this.contentEl.removeAttribute('tabindex')

    if (autoFocus) {
      l_enableFocusableElements(this.contentEl)
      l_trapFocus(this)
    }
  }

  setCollapsedAria(elementToFocus = this.triggerEl, enableTrapFocus = true) {
    this.removeAttribute('open')

    this.triggerEl.setAttribute('aria-expanded', 'false')
    this.contentEl.setAttribute('aria-hidden', 'true')
    this.contentEl.setAttribute('tabindex', '-1')

    l_disableFocusableElements(this.contentEl)
    enableTrapFocus && l_removeTrapFocus(elementToFocus)
  }
}

customElements.define('l-collapsible-tab', LTACollapsibleTab)

/*****************************************
 * [COMPONENT]: SWIPER CAROUSEL
 ******************************************/

class LTASwiperCarousel extends HTMLElement {
  constructor() {
    super()
    this._onResize = this._onResize.bind(this)
    this._swiperInitialized = false
  }

  connectedCallback() {
    if (window?.Swiper) {
      this.setupCarousel()

      return
    }

    document.addEventListener('l:vendor:loaded', () => {
      this.setupCarousel()
    })
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this._onResize)
  }

  setupCarousel() {
    const configNode = this.querySelector('[data-carousel-config]')
    if (!configNode) return

    this.carouselConfig = JSON.parse(configNode.textContent)
    if (!this.carouselConfig) return

    this.swiperSlideEls = this.querySelectorAll('.swiper-slide')

    this.classList.remove('is-placeholder')
    this.swiperSlideEls.forEach((swiperSlide) => swiperSlide.classList.remove('l-hidden'))

    this.swiperCarousel = new window.Swiper(this, this.carouselConfig)
    this._swiperInitialized = true

    // Design mode scroll
    if (window.Shopify?.designMode) {
      this.designModeScroll()
    }

    // Set up navigation top position
    const hasNavigationTarget = this.carouselConfig?.navigation && this.carouselConfig.navigation.navigationHeightTarget
    if (hasNavigationTarget) {
      this.setupNavigation()
      window.addEventListener('resize', this._onResize)
    }

    // Dispatch event
    if (this.dataset.dispatchEvent === 'true') {
      this.dispatchEvent(
        new CustomEvent('l:swiper:carousel:loaded', {
          bubbles: true,
          detail: {
            id: this.id || this.dataset.id || 'default',
          },
        })
      )
    }
  }

  _onResize() {
    // Re-setup navigation when window resizes
    if (this._swiperInitialized && this.carouselConfig?.navigation?.navigationHeightTarget) {
      this.setupNavigation()
    }
  }

  setupNavigation() {
    const navigation = this.carouselConfig?.navigation
    if (!navigation || !navigation.navigationHeightTarget) return

    const targetEl = this.querySelector(navigation.navigationHeightTarget)
    if (!targetEl) return

    const navTop = `${targetEl.offsetHeight / 2}px`

    const nextNavEls = document.querySelectorAll(navigation.nextEl)
    const prevNavEls = document.querySelectorAll(navigation.prevEl)

    nextNavEls.forEach((el) => el.style.setProperty('--l-swiper-top', navTop))
    prevNavEls.forEach((el) => el.style.setProperty('--l-swiper-top', navTop))
  }

  designModeScroll() {
    document.addEventListener('shopify:block:select', (event) => {
      const targetEl = event.target

      this.swiperSlideEls.forEach((swiperSlide, index0) => {
        if (targetEl === swiperSlide) this.slideTo(index0)
      })
    })
  }

  slideTo(index = 0) {
    if (this.swiperCarousel && typeof this.swiperCarousel.slideTo === 'function') {
      this.swiperCarousel.slideTo(index)
    }
  }
}

customElements.define('l-swiper-carousel', LTASwiperCarousel)

/*****************************************
 * [COMPONENT]: MEDIA VIDEO
 ******************************************/



/*****************************************
 * [COMPONENT]: MEDIA VIDEO
 ******************************************/

class LTAMediaVideo extends HTMLElement {
  constructor() {
    super()
  }

  connectedCallback() {
    // Elements
    this.videoContainerEl = this.querySelector('[data-video-container]') || this

    // Click
    this.addEventListener('click', (event) => {
      const hasLoaded = this.classList.contains('is-loaded')
      const isPlaying = this.classList.contains('is-playing')

      hasLoaded ? (isPlaying ? this.pauseVideo() : this.playVideo()) : this.initPlayer(event)
    })
  }

  initPlayer(event) {
    event.preventDefault()

    const templateEl = this.querySelector('template')

    if (!templateEl) return

    this.pauseOtherVideos()

    // Add template content to the element
    const templateContentEl = templateEl.content.cloneNode(true)

    this.videoContainerEl.appendChild(templateContentEl)
    this.classList.add('is-loaded', 'is-playing')
  }

  playVideo() {
    this.pauseOtherVideos()
    l_playMedia(this)

    this.classList.add('is-playing')
    this.classList.remove('is-paused')
  }

  pauseVideo() {
    l_pauseAllMedia(this)

    this.classList.remove('is-playing')
    this.classList.add('is-paused')
  }

  pauseOtherVideos() {
    l_pauseAllMedia(this.parentElement)

    this.parentElement.querySelectorAll('l-media-video').forEach((videoEl) => {
      const isLoaded = videoEl.classList.contains('is-loaded')

      if (isLoaded) {
        videoEl.classList.remove('is-playing')
        videoEl.classList.add('is-paused')
      }
    })
  }
}

customElements.define('l-media-video', LTAMediaVideo)

/*****************************************
 * [COMPONENT]: ATC BUTTON
 ******************************************/

class LTACTAButton extends HTMLElement {
  constructor() {
    super()

    this.handleAtcBtnClick = this.handleAtcBtnClick.bind(this)
  }

  connectedCallback() {
    this.buttonEl = this.querySelector('button')
    if (this.buttonEl) {
      this.buttonEl.addEventListener('click', this.handleAtcBtnClick)
    }
  }

  disconnectedCallback() {
    if (this.buttonEl) {
      this.buttonEl.removeEventListener('click', this.handleAtcBtnClick)
    }
  }

  handleAtcBtnClick() {
    if (!this.buttonEl) return

    this.buttonEl.classList.add('is-loading', 'l-button-loading')

    const { variantId, quantity, sellingPlanId } = this.dataset
    const config = {
      id: variantId,
      quantity: parseInt(quantity),
      ...(sellingPlanId ? { selling_plan: sellingPlanId } : {}),
    }

    const formData = ltaCartService.createConfigFromItems([config])

    ltaCartService
      .addToCart(formData, 'l-atc-button')
      .then(console.log)
      .catch(console.error)
      .finally(() => {
        this.buttonEl.classList.remove('is-loading', 'l-button-loading')
      })
  }
}

customElements.define('l-atc-button', LTACTAButton)

/*****************************************
 * [COMPONENT]: UPSELL PRODUCT CARD
 ******************************************/
if (!customElements.get('l-product-upsell-card')) {
  class LTAProductUpsellCard extends HTMLElement {
    constructor() {
      super()

      this.onAddToCart = this.onAddToCart.bind(this)
    }

    connectedCallback() {
      this.atcBtnEl = this.querySelector('[data-atc]')
      this.selectedVariantID = this.dataset.defaultVariantId

      if (this.atcBtnEl) {
        this.atcBtnEl.addEventListener('click', this.onAddToCart)
      }
    }

    onAddToCart() {
      console.log(this.atcBtnEl);
      console.log(this.selectedVariantID);

      if (!this.atcBtnEl || !this.selectedVariantID) return

      this.atcBtnEl.classList.add('is-loading')

      const config = ltaCartService.createConfigFromItems([{ id: this.selectedVariantID, quantity: 1 }])

      ltaCartService.addToCart(config, 'l-product-upsell-card').finally(() => {
        l_debounce(() => {
          if (this.atcBtnEl) this.atcBtnEl.classList.remove('is-loading')
        }, 500)()
      })
    }
  }

  customElements.define('l-product-upsell-card', LTAProductUpsellCard)
}

/************************************************************************************

* RUN GLOBAL FUNCTIONALITIES / RUN BASE FUNCTIONALITIES

*************************************************************************************/

function l_initializeGlobalEventListeners() {
  // Initialize on DOM ready
  window.addEventListener('DOMContentLoaded', () => {
    l_addClassesToRteTables()
    l_smoothScrollOnAnchorTags()
  })
}

l_initializeGlobalEventListeners()

